'use client'

import {
    Container,
    Stack,
    Flex,
    Box,
    Text,
    Icon,
    useToast,
} from '@chakra-ui/react'
import { FaApple, FaGooglePlay } from 'react-icons/fa'
import { motion } from 'framer-motion'
import { useTranslation } from 'react-i18next'
import header_app from './assets/images/header_app.png'

// Motion components
const MotionBox = motion(Box)
const MotionFlex = motion(Flex)
const MotionStack = motion(Stack)

export default function HeaderContent({ headerContentRef, featuresRef }) {
    const { t } = useTranslation()
    const toast = useToast()

    // Animation variants
    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: {
                staggerChildren: 0.15,
                delayChildren: 0.2
            }
        }
    }

    const itemVariants = {
        hidden: { opacity: 0, y: 30 },
        visible: {
            opacity: 1,
            y: 0,
            transition: {
                duration: 0.6,
                ease: [0.6, -0.05, 0.01, 0.99]
            }
        }
    }

    return (
        <Box  pt={10} ref={headerContentRef} w="100%" position="relative" overflow="hidden">
            {/* Animated Background Blobs */}
            <MotionBox
                position="absolute"
                top="5%"
                left="-5%"
                w="400px"
                h="400px"
                borderRadius="full"
                // bgGradient="radial(cyan.400, transparent)"
                filter="blur(80px)"
                opacity={0.3}
                animate={{
                    x: [0, 100, 0],
                    y: [0, 50, 0],
                    scale: [1, 1.2, 1]
                }}
                transition={{
                    duration: 15,
                    repeat: Infinity,
                    ease: "easeInOut"
                }}
            />
            <MotionBox
                position="absolute"
                bottom="5%"
                right="-5%"
                w="500px"
                h="500px"
                borderRadius="full"
                bgGradient="radial(blue.500, transparent)"
                filter="blur(100px)"
                opacity={0.2}
                animate={{
                    x: [0, -50, 0],
                    y: [0, -80, 0],
                    scale: [1, 1.3, 1]
                }}
                transition={{
                    duration: 20,
                    repeat: Infinity,
                    ease: "easeInOut"
                }}
            />

            <Container px={{ base: 4, md: 16 ,xl:8 }} maxW="6xl" position="relative" zIndex={1}>
                <MotionStack
                    align="center"
                    spacing={{ base: 12, md: 16 }}
                    py={{ base: 20, md: 28 }}
                    direction={{ base: 'column', md: 'row' }}
                    variants={containerVariants}
                    initial="hidden"
                    animate="visible"
                >
                    {/* Left Content */}
                    <Stack flex={1} spacing={{ base: 6, md: 8 }} zIndex={2}>
                        {/* Heading */}
                        <MotionBox variants={itemVariants}>
                            <MotionBox
                                as="h1"
                                fontSize={{ base: '3xl', sm: '4xl', lg: '5xl' }}
                                fontWeight={800}
                                lineHeight={1.1}
                                mb={2}
                            >
                                <MotionBox
                                    as="span"
                                    display="block"
                                    color="white"
                                    position="relative"
                                    _after={{
                                        content: "''",
                                        position: 'absolute',
                                        bottom: 0,
                                        left: 0,
                                        width: '100%',
                                        height: '30%',
                                        bg: 'blue.400',
                                        zIndex: -1,
                                    }}
                                >
                                    {t("Explorez l'exception!")}
                                </MotionBox>
                                <MotionBox
                                    as="span"
                                    display="block"
                                    bgGradient="linear(to-r, cyan.400, blue.500, purple.500)"
                                    bgClip="text"
                                    mt={2}
                                    initial={{ opacity: 0, x: -20 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    transition={{ delay: 0.5, duration: 0.8 }}
                                >
                                    {t("Profitez des offres APA.")}
                                </MotionBox>
                            </MotionBox>
                        </MotionBox>

                        {/* Description */}
                        <MotionBox variants={itemVariants}>
                            <Text
                                color="white"
                                fontSize={{ base: 'md', lg: 'lg' }}
                                lineHeight={1.8}
                                maxW="600px"
                            >
                                {t("APA, l’application incontournable pour l’achat, la location, et les offres exclusives sur les biens immobiliers. Explorez, économisez, et accédez aux meilleurs biens et opportunités du marché.")}
                            </Text>
                        </MotionBox>

                        {/* Download Buttons */}
                        <MotionFlex
                            gap={4}
                            flexDirection={{ base: 'column', sm: 'row' }}
                            variants={itemVariants}
                            zIndex={999}
                        >
                            {/* Play Store Button */}
                            <MotionBox
                                whileHover={{ scale: 1.05, y: -5 }}
                                whileTap={{ scale: 0.95 }}
                            >
                                <Flex
                                    as="a"
                                    align="center"
                                    p={3}
                                    px={5}
                                    borderRadius="xl"
                                    bg="black"
                                    border="1px solid"
                                    borderColor="whiteAlpha.300"
                                    cursor="pointer"
                                    position="relative"
                                    overflow="hidden"
                                    boxShadow="0 10px 40px -10px rgba(0,0,0,0.4)"

                                href="/Release/android/APA_PREMIUM_V1.3.4.apk"
                                download


                                    // onClick={(e) => {
                                    //     e.preventDefault()
                                    //     toast({
                                    //         title: "Bientôt disponible sur le Play Store !",
                                    //         position: "top",
                                    //         status: "info",
                                    //         isClosable: true,
                                    //         duration: 3000
                                    //     })
                                    // }}
                                >
                                    {/* Shimmer effect */}
                                    <MotionBox
                                        position="absolute"
                                        top={0}
                                        left="-100%"
                                        width="100%"
                                        height="100%"
                                        bgGradient="linear(to-r, transparent, whiteAlpha.200, transparent)"
                                        initial={{ left: '-100%' }}
                                        whileHover={{ left: '100%' }}
                                        transition={{ duration: 0.6 }}
                                    />
                                    <Icon as={FaGooglePlay} boxSize={6} color="white" zIndex={1} />
                                    <Box ml={3} zIndex={1}>
                                        <Text fontSize="md" fontWeight="bold" color="white">
                                            Android
                                        </Text>
                                        <Text fontSize="sm" color="whiteAlpha.800">
                                            {t("Télécharger maintenant")}
                                        </Text>
                                    </Box>
                                </Flex>
                            </MotionBox>

                            {/* App Store Button */}
                            <MotionBox
                                whileHover={{ scale: 1.05, y: -5 }}
                                whileTap={{ scale: 0.95 }}
                            >
                                <Flex
                                    as="button"
                                    align="center"
                                    p={3}
                                    px={5}
                                    borderRadius="xl"
                                    bgGradient="linear(to-r, cyan.500, blue.500)"
                                    cursor="pointer"
                                    position="relative"
                                    overflow="hidden"
                                    boxShadow="0 10px 40px -10px rgba(6, 182, 212, 0.6)"
                                    onClick={() => window.open("https://apps.apple.com/ma/app/apa-premium/id6480427570?l=fr-FR", '_blank')}
                                >
                                    {/* Shimmer effect */}
                                    <MotionBox
                                        position="absolute"
                                        top={0}
                                        left="-100%"
                                        width="100%"
                                        height="100%"
                                        bgGradient="linear(to-r, transparent, whiteAlpha.300, transparent)"
                                        initial={{ left: '-100%' }}
                                        whileHover={{ left: '100%' }}
                                        transition={{ duration: 0.6 }}
                                    />
                                    <Icon as={FaApple} boxSize={6} color="white" zIndex={1} />
                                    <Box ml={3} zIndex={1}>
                                        <Text fontSize="md" fontWeight="bold" color="white">
                                            App Store
                                        </Text>
                                        <Text fontSize="sm" color="whiteAlpha.900">
                                            {t("Télécharger maintenant")}
                                        </Text>
                                    </Box>
                                </Flex>
                            </MotionBox>
                        </MotionFlex>

                        {/* Stats */}
                        {/* <MotionFlex
                            gap={8}
                            variants={itemVariants}
                            flexWrap="wrap"
                            pt={4}
                        >
                            {[
                                { value: '10K+', label: 'Téléchargements' },
                                { value: '4.8★', label: 'Note moyenne' },
                                { value: '1000+', label: 'Propriétés' }
                            ].map((stat, i) => (
                                <MotionBox
                                    key={i}
                                    initial={{ opacity: 0, scale: 0.5 }}
                                    animate={{ opacity: 1, scale: 1 }}
                                    transition={{ delay: 1 + i * 0.1, duration: 0.5 }}
                                >
                                    <Text fontSize="2xl" fontWeight="bold" color="cyan.400">
                                        {stat.value}
                                    </Text>
                                    <Text fontSize="sm" color="whiteAlpha.700">
                                        {stat.label}
                                    </Text>
                                </MotionBox>
                            ))}
                        </MotionFlex> */}
                    </Stack>

                    {/* Right Image */}
                    <Flex flex={1} justify="center" align="center" position="relative" w="full" zIndex={2}>
                        <MotionBox
                            position="relative"
                            w="100%"
                            maxW="500px"
                            initial={{ opacity: 0, scale: 0.8, rotateY: -20 }}
                            animate={{ opacity: 1, scale: 1, rotateY: 0 }}
                            transition={{ duration: 1, ease: [0.6, -0.05, 0.01, 0.99] }}
                        >
                            {/* Phone Mockup with Floating Animation */}
                            <MotionBox
                                animate={{
                                    y: [0, -20, 0],
                                    rotateZ: [0, 2, 0, -2, 0]
                                }}
                                transition={{
                                    duration: 6,
                                    repeat: Infinity,
                                    ease: "easeInOut"
                                }}
                            >
                                <Box
                                    position="relative"
                                    _before={{
                                        content: '""',
                                        position: 'absolute',
                                        top: '50%',
                                        left: '50%',
                                        transform: 'translate(-50%, -50%)',
                                        width: '110%',
                                        height: '110%',
                                        bgGradient: 'radial(cyan.400, transparent)',
                                        filter: 'blur(60px)',
                                        opacity: 0.4,
                                        zIndex: -1
                                    }}
                                >
                                    <Box
                                        as="img"
                                        src={header_app}
                                        alt="APA Premium App"
                                        w="100%"
                                        h="auto"
                                        borderRadius="2xl"
                                        boxShadow="0 30px 60px -15px rgba(0, 0, 0, 0.5)"
                                    />
                                </Box>
                            </MotionBox>

                            {/* Floating Badge 1 - Top Right */}
                            <MotionBox
                                position="absolute"
                                top="15%"
                                right="-10%"
                                bg="white"
                                p={3}
                                borderRadius="xl"
                                boxShadow="0 20px 40px -10px rgba(0,0,0,0.3)"
                                animate={{
                                    y: [0, -15, 0],
                                    rotate: [0, 5, 0]
                                }}
                                transition={{
                                    duration: 3,
                                    repeat: Infinity,
                                    ease: "easeInOut"
                                }}
                            >
                                <Text fontSize="2xl">⭐</Text>
                                <Text fontSize="xs" fontWeight="bold" color="gray.700">
                                    Top Rated
                                </Text>
                            </MotionBox>

                            {/* Floating Badge 2 - Bottom Left */}
                            <MotionBox
                                position="absolute"
                                bottom="20%"
                                left="-10%"
                                bgGradient="linear(to-br, cyan.400, blue.500)"
                                p={3}
                                borderRadius="xl"
                                boxShadow="0 20px 40px -10px rgba(6, 182, 212, 0.5)"
                                animate={{
                                    y: [0, 15, 0],
                                    rotate: [0, -5, 0]
                                }}
                                transition={{
                                    duration: 4,
                                    repeat: Infinity,
                                    ease: "easeInOut",
                                    delay: 0.5
                                }}
                            >
                                <Text fontSize="2xl">🏠</Text>
                                <Text fontSize="xs" fontWeight="bold" color="white">
                                    {t("Meilleurs Prix")}
                                </Text>
                            </MotionBox>

                            {/* Floating Badge 3 - Right Middle */}
                            <MotionBox
                                position="absolute"
                                top="50%"
                                right="-5%"
                                bg="purple.500"
                                p={3}
                                borderRadius="xl"
                                boxShadow="0 20px 40px -10px rgba(128, 90, 213, 0.5)"
                                animate={{
                                    x: [0, 10, 0],
                                    scale: [1, 1.1, 1]
                                }}
                                transition={{
                                    duration: 2.5,
                                    repeat: Infinity,
                                    ease: "easeInOut"
                                }}
                            >
                                <Text fontSize="xl">🔥</Text>
                            </MotionBox>
                        </MotionBox>
                    </Flex>
                </MotionStack>
            </Container>
        </Box>
    )
}